import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { X, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Shown on all logged-in pages until email is verified.
 * Dismissible for the current session only.
 */
const VerifyEmailBanner = () => {
  const { user, resendVerification } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);

  if (!user || user.email_verified || dismissed) return null;

  const handleResend = async () => {
    setSending(true);
    try {
      await resendVerification();
      toast.success('Verification email sent. Check your inbox.', {
        description: 'For now the link is logged to the server console (email delivery is still being set up).',
      });
    } catch (e) {
      toast.error("We couldn't send the email. Try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="relative z-50 w-full bg-amber-50 border-b border-amber-200 px-4 py-2.5 text-sm text-amber-900 flex items-center justify-center gap-2 sm:gap-3"
      data-testid="verify-email-banner"
    >
      <Mail className="h-4 w-4 flex-shrink-0" />
      <span className="flex-1 text-center sm:text-left">
        Verify your email to message landlords.
      </span>
      <button
        onClick={handleResend}
        disabled={sending}
        className="font-semibold underline underline-offset-2 hover:text-amber-700 disabled:opacity-60"
        data-testid="resend-verification-btn"
      >
        {sending ? (
          <span className="inline-flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Sending…
          </span>
        ) : (
          'Resend email'
        )}
      </button>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="relative z-10 p-2 -mr-1 rounded-full hover:bg-amber-100 active:bg-amber-200"
        data-testid="dismiss-verify-banner"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default VerifyEmailBanner;
